import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import { addDays, format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { AlertTriangle, Check, ChevronDown, Download, Link2Off, Loader2, Plus, Trash2, Upload } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { toneSoftClasses, toneTextClasses } from "@/lib/tones";
import { createLocalDate, formatDateOnly, formatLocalDate } from "@/utils/dateUtils";
import { centsToReaisInput, formatCentsBRL, formatPercent, parseReaisToCents } from "@/utils/money";
import { useEnviarDocumentoAoOmie, useOmieCompetencias, useTrazerDocumentoDoOmie } from "@/hooks/use-finance-omie";
import { useToast } from "@/hooks/use-toast";
import { formatApiError } from "@/lib/api-error";
import { useInviteReceiptTexts } from "@/hooks/use-invite-receipt-texts";
import { CampoCodigoProposta } from "@/components/finance/proposta/CampoCodigoProposta";
import { CampoEditavel } from "@/components/finance/proposta/CampoEditavel";
import { SeletorDeArranjo } from "@/components/finance/proposta/ArranjoDeFaturamento";
import { DadosDaAgenciaDialog } from "@/components/finance/proposta/DadosDaAgenciaDialog";
import {
  nomeDaEmpresa,
  tituloDoArranjo,
  type EscolhaDeArranjo,
} from "@/components/finance/proposta/arranjo-de-faturamento";
import { identificacaoDaAgencia, type CampoDoCadastro } from "@/components/finance/proposta/dados-da-agencia";
import type {
  InviteReceiptText,
  PatchAgenciaDaPropostaBody,
  PatchPropostaBody,
  FaturaPorCompetenciaMensal,
  ImpostosDaProposta,
  PatchPropostaItemBody,
  PropostaDetail,
  PropostaGrupo,
  PropostaItem,
  RegimeVagaFixa,
  VarianteDoModelo,
} from "@/types/finance";

/** Same as BILLING_FORECAST_DAYS and DEFAULT_RECEIPT_TERM_DAYS in the API. */
const PREVISAO_FATURAMENTO_DIAS = 7;
const PRAZO_RECEBIMENTO_PADRAO_DIAS = 30;
const NOTAS_DA_AGENCIA = "agencia";
const NOTAS_POR_MES = "mes";
const NOTAS_POR_PERIODO = "periodo";
/** Same as DEFAULT_FAISCA_COMMISSION_PERCENTUAL in the API. */
const DEFAULT_COMMISSION_PERCENT = 10;
/** Same as the invite's fallback when a period has no receipt text nor date. */
const RECEIPT_TEXT_WITHOUT_GUIDANCE = "Informação sendo alinhada com a contratante.";
/** Same as AGENCY_TABLE_FEE_PERCENTUAL and FEE_DUE_DAYS_AFTER_START in the API. */
const TAXA_DE_TABELA_VAGA_FIXA = 100;
const VENCIMENTO_VAGA_FIXA_DIAS = 15;
const MODALIDADES = [
  { valor: "frila", rotulo: "Frila" },
  { valor: "fixa", rotulo: "Vaga fixa" },
] as const;
const REGIMES: Array<{ valor: RegimeVagaFixa; rotulo: string }> = [
  { valor: "clt", rotulo: "CLT" },
  { valor: "pj", rotulo: "PJ" },
];

interface EtapaDadosProps {
  proposta: PropostaDetail;
  editavel: boolean;
  salvarProposta: (patch: PatchPropostaBody) => void;
  salvarAgencia: (patch: PatchAgenciaDaPropostaBody) => void;
  salvarCadastroDaAgencia: (patch: PatchAgenciaDaPropostaBody) => Promise<void>;
  salvarCnpjDoFreelancer: (freelancerId: string, cnpj: string | null) => void;
  onAdicionarPeriodos: () => void;
  onNewPeriod: () => void;
  onRemoverItem: (item: PropostaItem, grupo: PropostaGrupo) => void;
  itemDestacado: string | null;
  camposComPendencia: Set<string>;
}

function numeroOuNulo(texto: string): number | null {
  const numero = Number(texto.replace(",", "."));
  return texto.trim() !== "" && Number.isFinite(numero) ? numero : null;
}

function rotuloCompetencia(competencia: string): string {
  const [mes, ano] = competencia.split(".");
  if (!mes || !ano) return competencia;
  const data = new Date(Number(ano), Number(mes) - 1, 1);
  return `${competencia} · ${format(data, "MMMM 'de' yyyy", { locale: ptBR })}`;
}

export function EtapaDados({
  proposta,
  editavel,
  salvarProposta,
  salvarAgencia,
  salvarCadastroDaAgencia,
  salvarCnpjDoFreelancer,
  onAdicionarPeriodos,
  onNewPeriod,
  onRemoverItem,
  itemDestacado,
  camposComPendencia,
}: EtapaDadosProps) {
  const competenciasQuery = useOmieCompetencias();
  const inviteTextsQuery = useInviteReceiptTexts({ agencia_id: proposta.agencia.id });
  const competencias = competenciasQuery.data?.items ?? [];
  const competenciaForaDaLista =
    proposta.competencia && !competencias.some((item) => item.competencia === proposta.competencia);
  const agencia = proposta.agencia;
  const vagaFixa = proposta.modalidade === "fixa";
  const totalPeriodos = proposta.grupos.reduce((soma, grupo) => soma + grupo.itens.length, 0);
  const [cadastroAberto, setCadastroAberto] = useState<{ campo: CampoDoCadastro | null } | null>(null);
  const editarNoCadastro = editavel ? (campo: CampoDoCadastro) => setCadastroAberto({ campo }) : undefined;

  const salvarAc = (valor: string) => {
    salvarProposta({ ac_contato: valor || null });
    if (valor && !agencia.contato_nome) salvarAgencia({ contato_nome: valor });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Identificação</CardTitle>
          <p className="text-sm text-muted-foreground">
            Os dados da agência ficam salvos no cadastro dela e já vêm preenchidos na próxima proposta.
          </p>
        </CardHeader>
        <CardContent className="space-y-5">
          <div id="dados-agencia" className="grid gap-4 rounded-lg border bg-muted/30 p-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1.5 sm:col-span-2 lg:col-span-4">
              <div className="flex flex-wrap items-center gap-2">
                <Link to={`/agencias/${agencia.id}`} className="text-sm font-semibold hover:underline">
                  {agencia.nome}
                </Link>
                {agencia.omie_vinculado ? (
                  <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
                    Vinculada ao Omie
                  </Badge>
                ) : (
                  <Link to="/financeiro?aba=cadastros-omie">
                    <Badge variant="outline" className="gap-1 border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100">
                      <Link2Off className="h-3 w-3" />
                      Sem vínculo com o Omie
                    </Badge>
                  </Link>
                )}
                {editavel && (
                  <button
                    type="button"
                    className="ml-auto text-xs font-medium text-primary hover:underline"
                    onClick={() => setCadastroAberto({ campo: null })}
                  >
                    Editar dados no cadastro da agência
                  </button>
                )}
              </div>
            </div>
            <CampoCodigoProposta
              proposta={proposta}
              disabled={!editavel}
              destaque={camposComPendencia.has("codigo")}
              onSalvar={(codigo) => salvarProposta({ codigo })}
            />
            <CampoEditavel
              id="campo-razao_social"
              label="Razão social"
              className="lg:col-span-2"
              valor={agencia.razao_social ?? ""}
              disabled={!editavel}
              destaque={camposComPendencia.has("razao_social")}
              onSalvar={(valor) => salvarAgencia({ razao_social: valor || null })}
            />
            <CampoEditavel
              id="campo-cnpj"
              label="CNPJ"
              valor={agencia.cnpj ?? ""}
              disabled={!editavel}
              inputMode="numeric"
              placeholder="00.000.000/0000-00"
              destaque={camposComPendencia.has("cnpj")}
              onSalvar={(valor) => salvarAgencia({ cnpj: valor || null })}
            />
            <CampoEditavel
              id="campo-endereco"
              label="Endereço da sede"
              className="sm:col-span-2 lg:col-span-3"
              valor={agencia.endereco ?? ""}
              disabled={!editavel}
              placeholder="Rua, número, bairro, cidade/UF, CEP"
              destaque={camposComPendencia.has("endereco")}
              ajuda="Aparece na identificação das partes da proposta"
              onSalvar={(valor) => salvarAgencia({ endereco: valor || null })}
            />
            <CampoDoCadastroDaAgencia
              label="Sigla"
              campo="sigla"
              valor={agencia.sigla}
              onEditar={editarNoCadastro}
            />
            <CampoDoCadastroDaAgencia
              label="E-mail do contato"
              campo="contato_email"
              valor={agencia.contato_email}
              onEditar={editarNoCadastro}
            />
            <CampoDoCadastroDaAgencia
              label="Cargo do contato"
              campo="contato_cargo"
              valor={agencia.contato_cargo}
              onEditar={editarNoCadastro}
            />
            <CampoDoCadastroDaAgencia
              label="Quem fatura (padrão)"
              campo="faturamento_empresa"
              valor={agencia.faturamento_empresa ? nomeDaEmpresa(agencia.faturamento_empresa) : null}
              vazio="Repete a última proposta"
              onEditar={editarNoCadastro}
            />
            <CampoDoCadastroDaAgencia
              label="Como fatura (padrão)"
              className="sm:col-span-2 lg:col-span-4"
              campo="faturamento_arranjo"
              valor={agencia.faturamento_arranjo ? tituloDoArranjo(agencia.faturamento_arranjo) : null}
              vazio="Repete a última proposta"
              onEditar={editarNoCadastro}
            />
            <div className="sm:col-span-2 lg:col-span-4">
              <ComoSaiNaProposta proposta={proposta} />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1.5">
              <Label htmlFor="campo-competencia" className="text-xs font-medium text-muted-foreground">
                Competência
              </Label>
              <Select
                value={proposta.competencia ?? undefined}
                disabled={!editavel}
                onValueChange={(valor) => salvarProposta({ competencia: valor })}
              >
                <SelectTrigger
                  id="campo-competencia"
                  className={cn(
                    camposComPendencia.has("competencia") && "border-amber-400 bg-amber-50/60",
                  )}
                >
                  <SelectValue placeholder="Selecione o mês" />
                </SelectTrigger>
                <SelectContent>
                  {competenciaForaDaLista && proposta.competencia && (
                    <SelectItem value={proposta.competencia}>
                      {rotuloCompetencia(proposta.competencia)}
                    </SelectItem>
                  )}
                  {competencias.map((item) => (
                    <SelectItem key={item.competencia} value={item.competencia}>
                      {rotuloCompetencia(item.competencia)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {proposta.competencia_sem_codigo ? (
                <p className="text-[11px] leading-snug text-amber-700">
                  {proposta.competencia_sem_codigo.mensagem}{" "}
                  <Link to="/financeiro?aba=competencias" className="font-medium underline underline-offset-2">
                    Abrir Competências
                  </Link>
                </p>
              ) : (
                <p className="text-[11px] text-muted-foreground">Mês de referência do lançamento no Omie</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="campo-modalidade" className="text-xs font-medium text-muted-foreground">
                Tipo de vaga
              </Label>
              <Select
                value={proposta.modalidade}
                disabled={!editavel}
                onValueChange={(valor) => salvarProposta({ modalidade: valor as PropostaDetail["modalidade"] })}
              >
                <SelectTrigger id="campo-modalidade">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MODALIDADES.map((modalidade) => (
                    <SelectItem key={modalidade.valor} value={modalidade.valor}>
                      {modalidade.rotulo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">
                {vagaFixa ? "Recrutamento: taxa sobre o 1º salário" : "Períodos com valor do profissional"}
              </p>
            </div>
            <CampoEditavel
              label={vagaFixa ? "Taxa de tabela da agência" : "Taxa da agência"}
              valor={formatPercent(proposta.taxa_percentual)}
              sufixo="%"
              inputMode="decimal"
              disabled={!editavel}
              ajuda={
                vagaFixa
                  ? `Do 1º salário, ${formatPercent(TAXA_DE_TABELA_VAGA_FIXA)}% na tabela`
                  : "Vem da última proposta desta agência"
              }
              onSalvar={(valor) => {
                const numero = numeroOuNulo(valor);
                if (numero != null && numero >= 0 && numero <= 100) {
                  salvarProposta({ taxa_percentual: numero });
                }
              }}
            />
            {!vagaFixa && (
              <CampoImpostos
                impostos={proposta.impostos_percentual}
                disabled={!editavel}
                onEscolher={(valor) => salvarProposta({ impostos_percentual: valor })}
                onTornarPadraoDaAgencia={(valor) => {
                  salvarAgencia({ impostos_percentual: valor || null });
                  salvarProposta({ impostos_percentual: null });
                }}
                onEditarNoCadastro={editarNoCadastro}
              />
            )}
            <CampoEditavel
              label="Aos cuidados de (AC)"
              valor={proposta.ac_contato ?? ""}
              disabled={!editavel}
              placeholder="Contato na agência"
              ajuda={
                <>
                  {agencia.contato_nome ? "Contato salvo da agência. " : "Fica salvo como contato da agência. "}
                  <LinkDoCadastro campo="contato_nome" onEditar={editarNoCadastro} />
                </>
              }
              onSalvar={salvarAc}
            />
            <CampoNotasDoInvite
              fatura={proposta.fatura_por_competencia_mensal}
              disabled={!editavel}
              onEscolher={(valor) => salvarProposta({ fatura_por_competencia_mensal: valor })}
              onTornarPadraoDaAgencia={(valor) => {
                salvarAgencia({ fatura_por_competencia_mensal: valor });
                salvarProposta({ fatura_por_competencia_mensal: null });
              }}
              onEditarNoCadastro={editarNoCadastro}
            />
            <CamposDaCompetenciaMensal
              fatura={proposta.fatura_por_competencia_mensal}
              disabled={!editavel}
              destaque={camposComPendencia.has("fatura_por_competencia_mensal")}
              onEscolher={salvarProposta}
              onTornarPadraoDaAgencia={(campo, valor) => {
                salvarAgencia({ [campo]: valor });
                salvarProposta({ [campo]: null });
              }}
              onEditarNoCadastro={editarNoCadastro}
            />
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold">Profissionais e períodos</h2>
            <p className="text-sm text-muted-foreground">
              Em cada período você escolhe por qual empresa faturar e como fica a comissão do profissional.
              Valor e datas do período atualizam o projeto.
            </p>
          </div>
          {editavel && (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" className="gap-1.5" onClick={onAdicionarPeriodos}>
                <Plus className="h-4 w-4" />
                Adicionar períodos
              </Button>
              <Button size="sm" variant="outline" className="gap-1.5" onClick={onNewPeriod}>
                <Plus className="h-4 w-4" />
                Novo período
              </Button>
            </div>
          )}
        </div>

        {editavel && !vagaFixa && (
          <FaturamentoEmLote
            proposta={proposta}
            onAplicar={(itens) => salvarProposta({ itens })}
            onSalvarComoPadrao={(escolha) =>
              salvarAgencia({ faturamento_empresa: escolha.empresa, faturamento_arranjo: escolha.arranjo })
            }
          />
        )}

        {proposta.grupos.map((grupo, indice) => (
          <ProfissionalCard
            key={grupo.freelancer.id}
            numero={indice + 1}
            grupo={grupo}
            taxaPercentual={proposta.taxa_percentual}
            vagaFixa={vagaFixa}
            empresas={proposta.empresas}
            editavel={editavel}
            podeRemover={totalPeriodos > 1}
            itemDestacado={itemDestacado}
            inviteTexts={inviteTextsQuery.data?.items ?? []}
            confirmationRequired={proposta.recebimento_confirmacao_exigida}
            variante={proposta.modelo_documento.em_uso}
            prazoRecebimentoDias={proposta.prazo_recebimento_dias}
            cnpjComPendencia={camposComPendencia.has(`cnpj-${grupo.freelancer.id}`)}
            salvarCnpj={(cnpj) => salvarCnpjDoFreelancer(grupo.freelancer.id, cnpj)}
            salvarItens={(itens) => salvarProposta({ itens })}
            onRemoverItem={(item) => onRemoverItem(item, grupo)}
          />
        ))}
      </div>
      {cadastroAberto && (
        <DadosDaAgenciaDialog
          proposta={proposta}
          campoInicial={cadastroAberto.campo}
          onSalvar={salvarCadastroDaAgencia}
          onFechar={() => setCadastroAberto(null)}
        />
      )}
    </div>
  );
}

type ModoAgencia = "taxa" | "total";
type ModoFreelancer = "cobrar" | "reter";
type Empresas = PropostaDetail["empresas"];

const MODOS_AGENCIA: Array<{ valor: ModoAgencia; rotulo: string }> = [
  { valor: "taxa", rotulo: "Fatura só a taxa" },
  { valor: "total", rotulo: "Fatura o total e repassa" },
];

const MODOS_FREELANCER: Array<{ valor: ModoFreelancer; rotulo: string }> = [
  { valor: "cobrar", rotulo: "Cobrada à parte" },
  { valor: "reter", rotulo: "Retida no repasse" },
];

const MISTO = "misto";

function valorUnico<T extends string>(valores: T[]): T | typeof MISTO {
  return valores.every((valor) => valor === valores[0]) ? valores[0] : MISTO;
}

function nomeCurto(empresa: Empresas[number]): string {
  return { corisco: "Corisco", faisca: "Faísca", gota_serena: "Gota Serena" }[empresa.chave] ?? empresa.nome;
}

interface EscolhaDeFaturamento {
  empresaAgencia: string;
  modoAgencia: string;
  empresaFreelancer: string;
  modoFreelancer: string;
}

function escolhaDoItem(item: PropostaItem): EscolhaDeFaturamento {
  return {
    empresaAgencia: item.empresa_agencia?.chave ?? "corisco",
    modoAgencia: item.modo_agencia,
    empresaFreelancer: item.empresa_freelancer?.chave ?? "faisca",
    modoFreelancer: item.modo_freelancer,
  };
}

function ControlesDeFaturamento({
  escolha,
  empresas,
  disabled,
  onChange,
  emLinha,
}: {
  escolha: EscolhaDeFaturamento;
  empresas: Empresas;
  disabled?: boolean;
  onChange: (patch: Omit<PatchPropostaItemBody, "id">) => void;
  emLinha?: boolean;
}) {
  const retem = escolha.modoAgencia === "total" && escolha.modoFreelancer === "reter";
  const opcoesEmpresa = empresas.map((empresa) => ({ valor: empresa.chave, rotulo: nomeCurto(empresa) }));
  return (
    <div className={cn("grid gap-3", emLinha ? "sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-end" : "sm:grid-cols-2")}>
      <fieldset className="space-y-1.5">
        <legend className="mb-1.5 text-xs font-medium text-muted-foreground">Faturar por</legend>
        <div className="flex flex-wrap gap-2">
          <SeletorCompacto
            valor={escolha.empresaAgencia}
            opcoes={opcoesEmpresa}
            disabled={disabled}
            rotuloAcessivel="Empresa que fatura a agência"
            onChange={(valor) => onChange({ empresa_agencia: valor as PatchPropostaItemBody["empresa_agencia"] })}
          />
          <SeletorCompacto
            valor={escolha.modoAgencia}
            opcoes={MODOS_AGENCIA}
            disabled={disabled}
            rotuloAcessivel="Como a agência é faturada"
            onChange={(valor) => onChange({ modo_agencia: valor as ModoAgencia })}
          />
        </div>
      </fieldset>
      <fieldset className="space-y-1.5">
        <legend className="mb-1.5 text-xs font-medium text-muted-foreground">Comissão do profissional</legend>
        <div className="flex flex-wrap gap-2">
          <SeletorCompacto
            valor={escolha.modoFreelancer}
            opcoes={MODOS_FREELANCER.map((modo) => ({
              ...modo,
              desabilitado: modo.valor === "reter" && escolha.modoAgencia === "taxa",
            }))}
            disabled={disabled}
            rotuloAcessivel="Como a comissão do profissional é cobrada"
            onChange={(valor) => onChange({ modo_freelancer: valor as ModoFreelancer })}
          />
          {!retem && (
            <SeletorCompacto
              valor={escolha.empresaFreelancer}
              opcoes={opcoesEmpresa}
              disabled={disabled}
              rotuloAcessivel="Empresa que cobra a comissão do profissional"
              onChange={(valor) =>
                onChange({ empresa_freelancer: valor as PatchPropostaItemBody["empresa_freelancer"] })
              }
            />
          )}
        </div>
      </fieldset>
    </div>
  );
}

function SeletorCompacto({
  valor,
  opcoes,
  onChange,
  disabled,
  rotuloAcessivel,
}: {
  valor: string;
  opcoes: Array<{ valor: string; rotulo: string; desabilitado?: boolean }>;
  onChange: (valor: string) => void;
  disabled?: boolean;
  rotuloAcessivel: string;
}) {
  return (
    <Select value={valor === MISTO ? undefined : valor} disabled={disabled} onValueChange={onChange}>
      <SelectTrigger aria-label={rotuloAcessivel} className="h-8 w-auto min-w-[130px] gap-2 bg-background text-xs">
        <SelectValue placeholder="Varia por período" />
      </SelectTrigger>
      <SelectContent>
        {opcoes.map((opcao) => (
          <SelectItem key={opcao.valor} value={opcao.valor} disabled={opcao.desabilitado} className="text-xs">
            {opcao.rotulo}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function FaturamentoEmLote({
  proposta,
  onAplicar,
  onSalvarComoPadrao,
}: {
  proposta: PropostaDetail;
  onAplicar: (itens: PatchPropostaItemBody[]) => void;
  onSalvarComoPadrao: (escolha: EscolhaDeArranjo) => void;
}) {
  const itens = proposta.grupos.flatMap((grupo) => grupo.itens);
  if (itens.length === 0) return null;
  const escolhas = itens.map(escolhaDoItem);
  const escolhaComum: EscolhaDeFaturamento = {
    empresaAgencia: valorUnico(escolhas.map((escolha) => escolha.empresaAgencia)),
    modoAgencia: valorUnico(escolhas.map((escolha) => escolha.modoAgencia)),
    empresaFreelancer: valorUnico(escolhas.map((escolha) => escolha.empresaFreelancer)),
    modoFreelancer: valorUnico(escolhas.map((escolha) => escolha.modoFreelancer)),
  };
  const { faturamento_empresa: empresaPadrao, faturamento_arranjo: arranjoPadrao } = proposta.agencia;
  const padrao = empresaPadrao && arranjoPadrao ? { empresa: empresaPadrao, arranjo: arranjoPadrao } : null;

  return (
    <div className="space-y-2 rounded-lg border bg-muted/30 px-4 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-sm font-medium">Faturamento de todos os períodos</span>
        <span className="text-xs text-muted-foreground">Dá para ajustar período a período abaixo.</span>
      </div>
      <SeletorDeArranjo
        itens={itens}
        empresas={proposta.empresas}
        onAplicar={(patch) => onAplicar(itens.map((item) => ({ id: item.id, ...patch })))}
        padrao={padrao}
        nomeDaAgencia={proposta.agencia.nome}
        onSalvarComoPadrao={onSalvarComoPadrao}
        personalizar={
          <ControlesDeFaturamento
            emLinha
            escolha={escolhaComum}
            empresas={proposta.empresas}
            onChange={(patch) => onAplicar(itens.map((item) => ({ id: item.id, ...patch })))}
          />
        }
      />
    </div>
  );
}

function resumoDoFaturamento(item: PropostaItem, empresas: Empresas): string {
  const nome = (chave: string | undefined, padrao: string) => {
    const empresa = empresas.find((candidata) => candidata.chave === chave);
    return empresa ? nomeCurto(empresa) : padrao;
  };
  const agencia = nome(item.empresa_agencia?.chave, "Corisco");
  if (item.modo_agencia === "total") {
    return item.modo_freelancer === "reter"
      ? `${agencia} fatura o total à agência e repassa o líquido ao profissional (uma OS).`
      : `${agencia} fatura o total e repassa o valor cheio; ${nome(item.empresa_freelancer?.chave, "Faísca")} cobra a comissão do profissional.`;
  }
  return `${agencia} fatura só a taxa; ${nome(item.empresa_freelancer?.chave, "Faísca")} cobra a comissão do profissional.`;
}

interface ProfissionalCardProps {
  numero: number;
  grupo: PropostaGrupo;
  taxaPercentual: number;
  vagaFixa: boolean;
  empresas: PropostaDetail["empresas"];
  editavel: boolean;
  podeRemover: boolean;
  itemDestacado: string | null;
  inviteTexts: InviteReceiptText[];
  confirmationRequired: boolean;
  variante: VarianteDoModelo;
  prazoRecebimentoDias: number | null;
  cnpjComPendencia: boolean;
  salvarCnpj: (cnpj: string | null) => void;
  salvarItens: (itens: PatchPropostaItemBody[]) => void;
  onRemoverItem: (item: PropostaItem) => void;
}

function ProfissionalCard({
  numero,
  grupo,
  taxaPercentual,
  vagaFixa,
  empresas,
  editavel,
  podeRemover,
  itemDestacado,
  inviteTexts,
  confirmationRequired,
  variante,
  prazoRecebimentoDias,
  cnpjComPendencia,
  salvarCnpj,
  salvarItens,
  onRemoverItem,
}: ProfissionalCardProps) {
  const { cnpj, documento_omie: documentoOmie } = grupo.freelancer;
  const paraTodosOsPeriodos = (patch: Omit<PatchPropostaItemBody, "id">) =>
    salvarItens(grupo.itens.map((item) => ({ id: item.id, ...patch })));

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 border-b bg-muted/30 px-4 py-3">
        <span className="rounded bg-primary/10 px-2 py-0.5 text-xs font-semibold tabular-nums text-primary">
          {String(numero).padStart(2, "0")}
        </span>
        <Link to={`/freelancers/${grupo.freelancer.id}`} className="text-sm font-semibold hover:underline">
          {grupo.freelancer.nome}
        </Link>
        {!grupo.freelancer.omie_vinculado && (
          <Link to="/financeiro?aba=cadastros-omie">
            <Badge variant="outline" className="gap-1 border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100">
              <Link2Off className="h-3 w-3" />
              Sem vínculo com o Omie
            </Badge>
          </Link>
        )}
      </div>
      <CardContent className="space-y-4 pt-4">
        <div className="grid gap-4 sm:grid-cols-[1fr_200px_180px]">
          <CampoEditavel
            label="Função / cargo"
            valor={grupo.cargo ?? ""}
            disabled={!editavel}
            onSalvar={(valor) => paraTodosOsPeriodos({ cargo: valor })}
          />
          <CampoEditavel
            id={`campo-cnpj-${grupo.freelancer.id}`}
            label="CNPJ"
            valor={cnpj ?? ""}
            disabled={!editavel}
            inputMode="numeric"
            placeholder={documentoOmie ?? "00.000.000/0000-00"}
            destaque={cnpjComPendencia}
            ajuda={!cnpj && documentoOmie ? "Os documentos usam o do Omie" : undefined}
            onSalvar={(valor) => salvarCnpj(valor || null)}
          />
          <ProfessionalCommissionField
            percent={grupo.comissao_percentual}
            editable={editavel}
            onSave={(percent) => paraTodosOsPeriodos({ comissao_percentual: percent })}
          />
        </div>
        {cnpj && documentoOmie && grupo.freelancer.omie_cliente_id && apenasDigitos(cnpj) !== apenasDigitos(documentoOmie) && (
          <DocumentoDivergente
            clienteId={grupo.freelancer.omie_cliente_id}
            documentoNosso={cnpj}
            documentoOmie={documentoOmie}
          />
        )}

        <div className="space-y-3">
          {grupo.itens.map((item, indice) =>
            vagaFixa ? (
              <ContratacaoFixaLinha
                key={item.id}
                item={item}
                taxaPercentual={taxaPercentual}
                empresas={empresas}
                editavel={editavel}
                podeRemover={podeRemover}
                destacado={itemDestacado === item.id}
                salvarItem={(patch) => salvarItens([{ id: item.id, ...patch }])}
                onRemover={() => onRemoverItem(item)}
              />
            ) : (
            <PeriodoLinha
              key={item.id}
              numero={indice + 1}
              item={item}
              taxaPercentual={taxaPercentual}
              empresas={empresas}
              editavel={editavel}
              podeRemover={podeRemover}
              destacado={itemDestacado === item.id}
              inviteTexts={inviteTexts}
              confirmationRequired={confirmationRequired}
              variante={variante}
              prazoRecebimentoDias={prazoRecebimentoDias}
              salvarItem={(patch) => salvarItens([{ id: item.id, ...patch }])}
              onRemover={() => onRemoverItem(item)}
            />
            ),
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ProfessionalCommissionField({
  percent,
  editable,
  onSave,
}: {
  percent: number;
  editable: boolean;
  onSave: (percent: number) => void;
}) {
  if (percent === 0) {
    return (
      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-muted-foreground">Comissão do profissional</Label>
        <div className="flex h-10 items-center rounded-md border bg-muted/40 px-3 text-sm font-medium">Isento</div>
        <div className="text-[11px] leading-snug text-muted-foreground">
          <button
            type="button"
            className="text-primary hover:underline disabled:opacity-50"
            disabled={!editable}
            onClick={() => onSave(DEFAULT_COMMISSION_PERCENT)}
          >
            Cobrar {DEFAULT_COMMISSION_PERCENT}% de novo
          </button>
        </div>
      </div>
    );
  }
  return (
    <CampoEditavel
      label="Comissão do profissional"
      valor={formatPercent(percent)}
      sufixo="%"
      inputMode="decimal"
      disabled={!editable}
      ajuda={
        <span>
          Lembrada para este profissional.{" "}
          <button
            type="button"
            className="text-primary hover:underline disabled:opacity-50"
            disabled={!editable}
            onClick={() => onSave(0)}
          >
            Isento
          </button>
        </span>
      }
      onSalvar={(valor) => {
        const numero = numeroOuNulo(valor);
        if (numero != null && numero >= 0 && numero <= 100) onSave(numero);
      }}
    />
  );
}

/** A comissão do profissional em reais no período, ao lado da taxa da agência: o percentual se edita no profissional. */
function ComissaoDoPeriodo({ item }: { item: PropostaItem }) {
  const isento = item.comissao_percentual === 0;
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-muted-foreground">Comissão do profissional</Label>
      <div className="flex h-10 items-center justify-between rounded-md border bg-muted/40 px-3 text-sm">
        <span>{isento ? "Isento" : item.comissao_cents != null ? centsToReaisInput(item.comissao_cents) : ""}</span>
        {!isento && <span className="text-muted-foreground">R$</span>}
      </div>
      <div className="text-[11px] leading-snug text-muted-foreground">
        {isento
          ? "Sem comissão para este profissional"
          : `${formatPercent(item.comissao_percentual)}% do valor profissional, cobrado do profissional`}
      </div>
    </div>
  );
}

interface PeriodoLinhaProps {
  numero: number;
  item: PropostaItem;
  taxaPercentual: number;
  empresas: PropostaDetail["empresas"];
  editavel: boolean;
  podeRemover: boolean;
  destacado: boolean;
  inviteTexts: InviteReceiptText[];
  confirmationRequired: boolean;
  variante: VarianteDoModelo;
  prazoRecebimentoDias: number | null;
  salvarItem: (patch: Omit<PatchPropostaItemBody, "id">) => void;
  onRemover: () => void;
}

function PeriodoLinha({
  numero,
  item,
  taxaPercentual,
  empresas,
  editavel,
  podeRemover,
  destacado,
  inviteTexts,
  confirmationRequired,
  variante,
  prazoRecebimentoDias,
  salvarItem,
  onRemover,
}: PeriodoLinhaProps) {
  const previsao = previsaoDasDatas(item, prazoRecebimentoDias);
  const plataformaPrevista =
    item.valor_profissional_cents != null
      ? Math.round((item.valor_profissional_cents * taxaPercentual) / 100)
      : null;

  return (
    <div
      id={`item-${item.id}`}
      className={cn(
        "rounded-lg border p-4 transition-colors",
        destacado && "border-amber-400 bg-amber-50/70",
      )}
    >
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm font-semibold">Período {String(numero).padStart(2, "0")}</p>
        {editavel && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 gap-1 text-xs text-muted-foreground hover:text-destructive"
            disabled={!podeRemover}
            title={podeRemover ? undefined : "A proposta precisa de ao menos um período"}
            onClick={onRemover}
          >
            <Trash2 className="h-3.5 w-3.5" />
            Tirar da proposta
          </Button>
        )}
      </div>
      <div className="space-y-3">
        <PeriodBlock title="Período">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <CampoEditavel
              label="Início"
              tipo="date"
              valor={item.data_inicio ?? ""}
              disabled={!editavel}
              destaque={!item.data_inicio}
              onSalvar={(valor) => valor && salvarItem({ data_inicio: valor })}
            />
            <CampoEditavel
              label="Fim"
              tipo="date"
              valor={item.data_conclusao ?? ""}
              disabled={!editavel}
              destaque={!item.data_conclusao}
              onSalvar={(valor) => valor && salvarItem({ data_conclusao: valor })}
            />
            <CampoEditavel
              label="Valor do profissional"
              valor={centsToReaisInput(item.valor_profissional_cents)}
              inputMode="decimal"
              sufixo="R$"
              disabled={!editavel}
              destaque={!item.valor_profissional_cents}
              onSalvar={(valor) => {
                const cents = parseReaisToCents(valor);
                if (cents != null && cents > 0) salvarItem({ valor_profissional_cents: cents });
              }}
            />
            <CampoEditavel
              label="Taxa da agência"
              valor={item.valor_plataforma_manual ? centsToReaisInput(item.valor_plataforma_cents) : ""}
              placeholder={plataformaPrevista != null ? centsToReaisInput(plataformaPrevista) : ""}
              inputMode="decimal"
              sufixo="R$"
              disabled={!editavel}
              ajuda={
                item.valor_plataforma_manual ? (
                  <button
                    type="button"
                    className="text-primary hover:underline disabled:opacity-50"
                    disabled={!editavel}
                    onClick={() => salvarItem({ valor_plataforma_cents: null })}
                  >
                    Valor ajustado à mão. Voltar para {formatPercent(taxaPercentual)}%
                  </button>
                ) : (
                  `${formatPercent(taxaPercentual)}% do valor profissional, cobrado da agência`
                )
              }
              onSalvar={(valor) => {
                if (valor === "") {
                  if (item.valor_plataforma_manual) salvarItem({ valor_plataforma_cents: null });
                  return;
                }
                const cents = parseReaisToCents(valor);
                if (cents != null) salvarItem({ valor_plataforma_cents: cents });
              }}
            />
            <ComissaoDoPeriodo item={item} />
          </div>
        </PeriodBlock>
        <PeriodBlock title="Omie" hint="Datas usadas nas ordens de serviço. Vazias, vale a previsão.">
          <div className="grid gap-3 sm:grid-cols-2">
            <CampoEditavel
              label="Data de faturamento com a agência"
              tipo="date"
              valor={item.data_faturamento ?? ""}
              disabled={!editavel}
              destaque={!item.data_faturamento && !previsao.faturamento}
              ajuda={
                <AjudaDaPrevisao
                  digitada={item.data_faturamento}
                  prevista={previsao.faturamento}
                  regra={`fim do período + ${PREVISAO_FATURAMENTO_DIAS} dias`}
                />
              }
              onSalvar={(valor) => salvarItem({ data_faturamento: valor || null })}
            />
            <CampoEditavel
              label="Previsão de recebimento da agência"
              tipo="date"
              valor={item.data_recebimento ?? ""}
              disabled={!editavel}
              destaque={!item.data_recebimento && !previsao.recebimento}
              ajuda={
                <AjudaDaPrevisao
                  digitada={item.data_recebimento}
                  prevista={previsao.recebimento}
                  regra={
                    prazoRecebimentoDias != null
                      ? `faturamento + ${prazoRecebimentoDias} dias, prazo desta agência`
                      : `faturamento + ${PRAZO_RECEBIMENTO_PADRAO_DIAS} dias`
                  }
                />
              }
              onSalvar={(valor) => salvarItem({ data_recebimento: valor || null })}
            />
          </div>
        </PeriodBlock>
        <PeriodBlock title="Invite" hint="O que o profissional lê no invite deste período.">
          <InviteFields
            item={item}
            editable={editavel}
            texts={inviteTexts}
            confirmationRequired={confirmationRequired}
            variant={variante}
            saveItem={salvarItem}
          >
            {retemComissao(item) ? (
              <>
                <CampoEditavel
                  label="Pagamento ao profissional"
                  tipo="date"
                  valor={item.data_pagamento_freelancer ?? ""}
                  disabled={!editavel}
                  ajuda={
                    <AjudaDaDataCombinada
                      combinada={item.data_pagamento_freelancer_manual}
                      regra="30 dias após o término"
                      disabled={!editavel}
                      onVoltar={() => salvarItem({ data_pagamento_freelancer: null })}
                    />
                  }
                  onSalvar={(valor) => salvarItem({ data_pagamento_freelancer: valor || null })}
                />
                <CampoEditavel
                  label="Envio da NF do profissional"
                  tipo="date"
                  valor={item.data_nf_freelancer ?? ""}
                  disabled={!editavel}
                  ajuda={
                    <AjudaDaDataCombinada
                      combinada={item.data_nf_freelancer_manual}
                      regra="7 dias antes do pagamento"
                      disabled={!editavel}
                      onVoltar={() => salvarItem({ data_nf_freelancer: null })}
                    />
                  }
                  onSalvar={(valor) => salvarItem({ data_nf_freelancer: valor || null })}
                />
              </>
            ) : (
              <CampoEditavel
                label="Data de repasse"
                tipo="date"
                valor={item.data_repasse ?? ""}
                disabled={!editavel}
                ajuda={
                  <AjudaDaDataCombinada
                    combinada={item.data_repasse_manual}
                    regra="7 dias após o recebimento"
                    disabled={!editavel}
                    onVoltar={() => salvarItem({ data_repasse: null })}
                  />
                }
                onSalvar={(valor) => salvarItem({ data_repasse: valor || null })}
              />
            )}
          </InviteFields>
        </PeriodBlock>
      </div>
      <div className="mt-3 space-y-2 rounded-md bg-muted/40 p-3">
        <SeletorDeArranjo
          itens={[item]}
          empresas={empresas}
          disabled={!editavel}
          onAplicar={salvarItem}
          personalizar={
            <div className="space-y-2">
              <ControlesDeFaturamento
                escolha={escolhaDoItem(item)}
                empresas={empresas}
                disabled={!editavel}
                onChange={salvarItem}
              />
              <p className="text-[11px] text-muted-foreground">{resumoDoFaturamento(item, empresas)}</p>
            </div>
          }
        />
        <ComoSaiNoInvite>
          <ComissaoNoInvite item={item} variante={variante} />
        </ComoSaiNoInvite>
      </div>
      <div
        className={cn(
          "mt-3 grid gap-2 border-t border-dashed pt-3 text-xs text-muted-foreground",
          item.valor_impostos_cents ? "sm:grid-cols-4" : "sm:grid-cols-3",
        )}
      >
        <span>
          Taxa da agência: <strong className="text-foreground">{formatCentsBRL(item.valor_plataforma_cents)}</strong>
        </span>
        {item.valor_impostos_cents ? (
          <span>
            Impostos: <strong className="text-foreground">{formatCentsBRL(item.valor_impostos_cents)}</strong>
          </span>
        ) : null}
        <span>
          Total da proposta: <strong className="text-foreground">{formatCentsBRL(item.total_cents)}</strong>
        </span>
        <span>
          {item.comissao_percentual === 0 ? (
            <>
              Comissão do profissional: <strong className="text-foreground">Isento</strong>
            </>
          ) : (
            <>
              Comissão do profissional ({formatPercent(item.comissao_percentual)}%):{" "}
              <strong className="text-foreground">{formatCentsBRL(item.comissao_cents)}</strong>
            </>
          )}
        </span>
      </div>
    </div>
  );
}

interface ContratacaoFixaLinhaProps {
  item: PropostaItem;
  taxaPercentual: number;
  empresas: PropostaDetail["empresas"];
  editavel: boolean;
  podeRemover: boolean;
  destacado: boolean;
  salvarItem: (patch: Omit<PatchPropostaItemBody, "id">) => void;
  onRemover: () => void;
}

/** Uma contratação de vaga fixa: o valor do item é o 1º salário e cada lado paga a taxa de tabela menos o desconto. */
function ContratacaoFixaLinha({
  item,
  taxaPercentual,
  empresas,
  editavel,
  podeRemover,
  destacado,
  salvarItem,
  onRemover,
}: ContratacaoFixaLinhaProps) {
  const salvarPercentual = (campo: "desconto_percentual" | "desconto_comissao_percentual") => (valor: string) => {
    const numero = valor === "" ? 0 : numeroOuNulo(valor);
    if (numero != null && numero >= 0 && numero <= 100) salvarItem({ [campo]: numero });
  };
  const opcoesEmpresa = empresas.map((empresa) => ({ valor: empresa.chave, rotulo: nomeCurto(empresa) }));

  return (
    <div
      id={`item-${item.id}`}
      className={cn("rounded-lg border p-4 transition-colors", destacado && "border-amber-400 bg-amber-50/70")}
    >
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm font-semibold">Contratação</p>
        {editavel && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 gap-1 text-xs text-muted-foreground hover:text-destructive"
            disabled={!podeRemover}
            title={podeRemover ? undefined : "A proposta precisa de ao menos uma contratação"}
            onClick={onRemover}
          >
            <Trash2 className="h-3.5 w-3.5" />
            Tirar da proposta
          </Button>
        )}
      </div>
      <div className="space-y-3">
        <PeriodBlock title="Vaga">
          <div className="grid gap-3 sm:grid-cols-3">
            <CampoEditavel
              label="Início"
              tipo="date"
              valor={item.data_inicio ?? ""}
              disabled={!editavel}
              destaque={!item.data_inicio}
              onSalvar={(valor) => valor && salvarItem({ data_inicio: valor })}
            />
            <div className="space-y-1.5">
              <Label htmlFor={`regime-${item.id}`} className="text-xs font-medium text-muted-foreground">
                Regime
              </Label>
              <Select
                value={item.tipo_contrato ?? undefined}
                disabled={!editavel}
                onValueChange={(valor) => salvarItem({ tipo_contrato: valor as RegimeVagaFixa })}
              >
                <SelectTrigger
                  id={`regime-${item.id}`}
                  className={cn(!item.tipo_contrato && "border-amber-400 bg-amber-50/60")}
                >
                  <SelectValue placeholder="CLT ou PJ" />
                </SelectTrigger>
                <SelectContent>
                  {REGIMES.map((regime) => (
                    <SelectItem key={regime.valor} value={regime.valor}>
                      {regime.rotulo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <CampoEditavel
              label="1º salário mensal bruto"
              valor={centsToReaisInput(item.valor_profissional_cents)}
              inputMode="decimal"
              sufixo="R$"
              disabled={!editavel}
              destaque={!item.valor_profissional_cents}
              ajuda="CLT: salário-base. PJ: valor mensal do contrato"
              onSalvar={(valor) => {
                const cents = parseReaisToCents(valor);
                if (cents != null && cents > 0) salvarItem({ valor_profissional_cents: cents });
              }}
            />
          </div>
        </PeriodBlock>
        <PeriodBlock title="Taxas" hint="Taxa de tabela sobre o 1º salário, menos o desconto de cortesia.">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <CampoEditavel
              label="Desconto para a agência"
              valor={formatPercent(item.desconto_percentual)}
              sufixo="%"
              inputMode="decimal"
              disabled={!editavel}
              ajuda={`Sobre a taxa de tabela de ${formatPercent(taxaPercentual)}%`}
              onSalvar={salvarPercentual("desconto_percentual")}
            />
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground">Taxa da agência</Label>
              <div className="flex h-10 items-center justify-between rounded-md border bg-muted/40 px-3 text-sm">
                <span>{centsToReaisInput(item.valor_plataforma_cents)}</span>
                <span className="text-muted-foreground">R$</span>
              </div>
              <div className="text-[11px] leading-snug text-muted-foreground">Cobrada da agência</div>
            </div>
            <CampoEditavel
              label="Desconto para o profissional"
              valor={formatPercent(item.desconto_comissao_percentual)}
              sufixo="%"
              inputMode="decimal"
              disabled={!editavel}
              ajuda={`Sobre a comissão de ${formatPercent(item.comissao_percentual)}% do profissional`}
              onSalvar={salvarPercentual("desconto_comissao_percentual")}
            />
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground">Taxa do profissional</Label>
              <div className="flex h-10 items-center justify-between rounded-md border bg-muted/40 px-3 text-sm">
                <span>{item.comissao_cents ? centsToReaisInput(item.comissao_cents) : "Nada a repassar"}</span>
                {item.comissao_cents ? <span className="text-muted-foreground">R$</span> : null}
              </div>
              <div className="text-[11px] leading-snug text-muted-foreground">Paga pelo profissional, contra nota</div>
            </div>
          </div>
        </PeriodBlock>
        <PeriodBlock title="Omie" hint="Datas usadas nas ordens de serviço. Vazias, vale a previsão.">
          <div className="grid gap-3 sm:grid-cols-3">
            <CampoEditavel
              label="Nota da taxa da agência"
              tipo="date"
              valor={item.data_faturamento ?? ""}
              disabled={!editavel}
              destaque={!item.data_faturamento && !item.data_faturamento_prevista}
              ajuda={
                <AjudaDaPrevisao
                  digitada={item.data_faturamento}
                  prevista={item.data_faturamento_prevista}
                  regra="data de início"
                />
              }
              onSalvar={(valor) => salvarItem({ data_faturamento: valor || null })}
            />
            <CampoEditavel
              label="Vencimento da taxa da agência"
              tipo="date"
              valor={item.data_recebimento ?? ""}
              disabled={!editavel}
              destaque={!item.data_recebimento && !item.data_recebimento_prevista}
              ajuda={
                <AjudaDaPrevisao
                  digitada={item.data_recebimento}
                  prevista={item.data_recebimento_prevista}
                  regra={`início + ${VENCIMENTO_VAGA_FIXA_DIAS} dias`}
                />
              }
              onSalvar={(valor) => salvarItem({ data_recebimento: valor || null })}
            />
            <CampoEditavel
              label="Vencimento da taxa do profissional"
              tipo="date"
              valor={item.data_repasse ?? ""}
              disabled={!editavel}
              ajuda={
                item.data_repasse_manual ? (
                  <button
                    type="button"
                    className="underline underline-offset-2"
                    disabled={!editavel}
                    onClick={() => salvarItem({ data_repasse: null })}
                  >
                    Voltar para 7 dias após o vencimento da agência
                  </button>
                ) : (
                  "7 dias após o vencimento da agência"
                )
              }
              onSalvar={(valor) => salvarItem({ data_repasse: valor || null })}
            />
          </div>
        </PeriodBlock>
      </div>
      <div className="mt-3 rounded-md bg-muted/40 p-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <fieldset className="space-y-1.5">
            <legend className="mb-1.5 text-xs font-medium text-muted-foreground">Empresa que cobra a agência</legend>
            <SeletorCompacto
              valor={item.empresa_agencia?.chave ?? "corisco"}
              opcoes={opcoesEmpresa}
              disabled={!editavel}
              rotuloAcessivel="Empresa que cobra a taxa da agência"
              onChange={(valor) => salvarItem({ empresa_agencia: valor as PatchPropostaItemBody["empresa_agencia"] })}
            />
          </fieldset>
          <fieldset className="space-y-1.5">
            <legend className="mb-1.5 text-xs font-medium text-muted-foreground">Empresa que cobra o profissional</legend>
            <SeletorCompacto
              valor={item.empresa_freelancer?.chave ?? "faisca"}
              opcoes={opcoesEmpresa}
              disabled={!editavel}
              rotuloAcessivel="Empresa que cobra a taxa do profissional"
              onChange={(valor) =>
                salvarItem({ empresa_freelancer: valor as PatchPropostaItemBody["empresa_freelancer"] })
              }
            />
          </fieldset>
        </div>
      </div>
    </div>
  );
}

function PeriodBlock({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="space-y-2 rounded-md border border-dashed p-3">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h4>
        {hint && <span className="text-[11px] text-muted-foreground">{hint}</span>}
      </div>
      {children}
    </section>
  );
}

function InviteFields({
  item,
  editable,
  texts,
  confirmationRequired,
  variant,
  saveItem,
  children,
}: {
  item: PropostaItem;
  editable: boolean;
  texts: InviteReceiptText[];
  confirmationRequired: boolean;
  variant: VarianteDoModelo;
  saveItem: (patch: Omit<PatchPropostaItemBody, "id">) => void;
  children: ReactNode;
}) {
  const agencyTexts = texts.filter((text) => text.agencia);
  const generalTexts = texts.filter((text) => !text.agencia);
  const pendingConfirmation = confirmationRequired && !item.recebimento_confirmado_em;
  const showCourtesy = item.comissao_percentual !== DEFAULT_COMMISSION_PERCENT || !!item.aviso_cortesia;
  const withoutGuidance = item.recebimento_texto === RECEIPT_TEXT_WITHOUT_GUIDANCE;
  const toggleWithoutGuidance = (checked: boolean) =>
    saveItem(
      checked
        ? { recebimento_texto: RECEIPT_TEXT_WITHOUT_GUIDANCE, ...(confirmationRequired && { recebimento_confirmado: true }) }
        : { recebimento_texto: null },
    );

  const textOption = (text: InviteReceiptText) => (
    <DropdownMenuItem
      key={text.id}
      className="flex-col items-start gap-0.5"
      onSelect={() => saveItem({ recebimento_texto: text.texto })}
    >
      <span className="text-xs font-medium">
        {text.titulo}
        {text.empresa ? ` (${text.empresa.nome})` : ""}
      </span>
      <span className="line-clamp-2 text-[11px] text-muted-foreground">{text.texto}</span>
    </DropdownMenuItem>
  );

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-start">
        <CampoEditavel
          id={`campo-recebimento_texto-${item.id}`}
          label="Orientação de faturamento"
          multilinha
          valor={item.recebimento_texto ?? ""}
          disabled={!editable || withoutGuidance}
          destaque={pendingConfirmation}
          placeholder="Ex.: nota entre os dias 1 e 5 de maio, para recebimento em 20/05/26"
          ajuda={
            withoutGuidance ? (
              "Sem orientação da agência por enquanto: o invite mostra este texto padrão."
            ) : pendingConfirmation ? (
              <span className="text-amber-700">
                Esta agência tem texto cadastrado: confirme o texto deste período antes de gerar os documentos.
              </span>
            ) : item.recebimento_confirmado_em ? (
              <span className="inline-flex items-center gap-1 text-emerald-700">
                <Check className="h-3 w-3" />
                Texto confirmado em {formatDateOnly(item.recebimento_confirmado_em)}
              </span>
            ) : (
              "Aparece no invite no lugar da data de recebimento da agência."
            )
          }
          onSalvar={(valor) => saveItem({ recebimento_texto: valor || null })}
        />
        <div className="flex flex-wrap gap-2 sm:pt-6">
          {texts.length > 0 && !withoutGuidance && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" size="sm" variant="outline" className="gap-1" disabled={!editable}>
                  Usar texto cadastrado
                  <ChevronDown className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                {agencyTexts.length > 0 && <DropdownMenuLabel className="text-xs">Desta agência</DropdownMenuLabel>}
                {agencyTexts.map(textOption)}
                {agencyTexts.length > 0 && generalTexts.length > 0 && <DropdownMenuSeparator />}
                {generalTexts.length > 0 && <DropdownMenuLabel className="text-xs">Gerais</DropdownMenuLabel>}
                {generalTexts.map(textOption)}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {pendingConfirmation && (
            <Button
              type="button"
              size="sm"
              className="gap-1"
              disabled={!editable || !item.recebimento_texto}
              title={item.recebimento_texto ? undefined : "Preencha a orientação antes de confirmar"}
              onClick={() => saveItem({ recebimento_confirmado: true })}
            >
              <Check className="h-3.5 w-3.5" />
              Confirmar texto
            </Button>
          )}
        </div>
      </div>
      <label className="flex w-fit items-center gap-2 text-xs">
        <Checkbox
          checked={withoutGuidance}
          disabled={!editable}
          onCheckedChange={(checked) => toggleWithoutGuidance(checked === true)}
        />
        Sem orientação de faturamento
      </label>
      <ComoSaiNoInvite>
        <ReceiptRow item={item} variant={variant} />
      </ComoSaiNoInvite>
      <div className="grid gap-3 sm:grid-cols-2">
        {children}
        {showCourtesy && (
          <CampoEditavel
            label="Aviso de cortesia (opcional)"
            valor={item.aviso_cortesia ?? ""}
            disabled={!editable}
            placeholder="Ex.: comissão reduzida neste período, por cortesia"
            ajuda={`A comissão deste período é diferente de ${DEFAULT_COMMISSION_PERCENT}%. Se preenchido, o aviso aparece no invite.`}
            onSalvar={(valor) => saveItem({ aviso_cortesia: valor || null })}
          />
        )}
      </div>
      {showCourtesy && (
        <ComoSaiNoInvite>
          <LinhaDoInvite rotulo="Observação">
            {item.aviso_cortesia || <span className="text-muted-foreground">Sem aviso: a linha não aparece.</span>}
          </LinhaDoInvite>
        </ComoSaiNoInvite>
      )}
      {variant === "corisco" && !!item.previa_invite?.parcelas.length && (
        <ComoSaiNoInvite titulo="Cronograma no invite">
          <CronogramaDoInvite parcelas={item.previa_invite.parcelas} />
        </ComoSaiNoInvite>
      )}
      {variant !== "gota_serena" && !!item.previa_invite?.pagamentos_freelancer?.length && (
        <ComoSaiNoInvite titulo="Nota fiscal e pagamento no invite">
          <PagamentosDoProfissional
            pagamentos={item.previa_invite.pagamentos_freelancer}
            empresa={item.empresa_agencia?.nome ?? "Faísca"}
          />
        </ComoSaiNoInvite>
      )}
    </div>
  );
}

/** Datas de faturamento e recebimento que valem quando o campo está vazio, e as que valeriam se fosse apagado. */
function previsaoDasDatas(item: PropostaItem, prazoRecebimentoDias: number | null) {
  const faturamento =
    item.data_faturamento_prevista ??
    (item.data_conclusao
      ? formatLocalDate(addDays(createLocalDate(item.data_conclusao), PREVISAO_FATURAMENTO_DIAS)) ?? null
      : null);
  const faturamentoEfetivo = item.data_faturamento ?? faturamento;
  const recebimento =
    item.data_recebimento_prevista ??
    (faturamentoEfetivo
      ? formatLocalDate(
          addDays(createLocalDate(faturamentoEfetivo), prazoRecebimentoDias ?? PRAZO_RECEBIMENTO_PADRAO_DIAS),
        ) ?? null
      : null);
  return { faturamento, recebimento };
}

function dataCurta(iso: string): string {
  return format(createLocalDate(iso), "dd/MM/yyyy");
}

function AjudaDaPrevisao({ digitada, prevista, regra }: { digitada: string | null; prevista: string | null; regra: string }) {
  if (!prevista) return null;
  if (digitada) {
    return digitada === prevista ? null : <span>Apague para voltar à previsão ({dataCurta(prevista)}).</span>;
  }
  return (
    <span className={toneTextClasses.info}>
      Por padrão: previsão <strong>{dataCurta(prevista)}</strong> ({regra}).
    </span>
  );
}

function ComoSaiNoInvite({ titulo = "Como sai no invite", children }: { titulo?: string; children: ReactNode }) {
  return (
    <details className={cn("group rounded-md border text-xs", toneSoftClasses.info)}>
      <summary className="flex cursor-pointer list-none items-center gap-1 px-2 py-1.5 font-medium [&::-webkit-details-marker]:hidden">
        <ChevronDown className="h-3.5 w-3.5 -rotate-90 transition-transform group-open:rotate-0" />
        {titulo}
      </summary>
      <div className="space-y-1.5 border-t border-tone-info-border bg-background px-3 py-2 text-foreground">{children}</div>
    </details>
  );
}

function LinhaDoInvite({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="grid gap-x-3 gap-y-0.5 sm:grid-cols-[11rem_1fr]">
      <span className="font-medium text-muted-foreground">{rotulo}</span>
      <span className="whitespace-pre-line">{children}</span>
    </div>
  );
}

function ReceiptRow({ item, variant }: { item: PropostaItem; variant: VarianteDoModelo }) {
  const receipt = item.previa_invite?.recebimento;
  const isForecast = !item.recebimento_texto && !!receipt;
  if (receipt) {
    return (
      <>
        <LinhaDoInvite rotulo="Recebimento">{receipt}</LinhaDoInvite>
        {isForecast && (
          <p className={toneTextClasses.info}>
            Previsão automática: some quando a orientação ou a data de faturamento forem preenchidas.
          </p>
        )}
      </>
    );
  }
  if (variant === "corisco") {
    return <p className="text-muted-foreground">Sem linha de recebimento: as datas saem no cronograma de notas.</p>;
  }
  const receiptDate = item.data_recebimento ?? item.data_recebimento_prevista;
  return (
    <LinhaDoInvite rotulo="Data de recebimento">
      {receiptDate ? dataCurta(receiptDate) : "Informação sendo alinhada com a contratante."}
    </LinhaDoInvite>
  );
}

function ComissaoNoInvite({ item, variante }: { item: PropostaItem; variante: VarianteDoModelo }) {
  const percentual = `${formatPercent(item.comissao_percentual)}%`;
  if (item.comissao_percentual === 0) {
    return <LinhaDoInvite rotulo="Comissão">Isento neste período: o profissional recebe o valor integral.</LinhaDoInvite>;
  }
  if (variante === "corisco") {
    return (
      <LinhaDoInvite rotulo={`Taxa de agenciamento Faísca (${percentual})`}>{formatCentsBRL(item.comissao_cents)}</LinhaDoInvite>
    );
  }
  if (item.modo_agencia === "total" && item.modo_freelancer === "reter") {
    const liquido =
      item.valor_profissional_cents != null && item.comissao_cents != null
        ? item.valor_profissional_cents - item.comissao_cents
        : null;
    return (
      <LinhaDoInvite rotulo="Valor líquido">
        {formatCentsBRL(liquido)} a receber de {item.empresa_agencia?.nome ?? "Corisco"}, já descontados {percentual} da
        plataforma ({formatCentsBRL(item.comissao_cents)})
      </LinhaDoInvite>
    );
  }
  return (
    <LinhaDoInvite rotulo={`Repasse ${percentual} plataforma`}>
      {item.data_repasse
        ? `${dataCurta(item.data_repasse)}${item.data_repasse_manual ? "" : " (previsão)"}`
        : "a definir"}{" "}
      ({formatCentsBRL(item.comissao_cents)})
    </LinhaDoInvite>
  );
}

function CronogramaDoInvite({ parcelas }: { parcelas: NonNullable<PropostaItem["previa_invite"]>["parcelas"] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left tabular-nums">
        <thead className="text-muted-foreground">
          <tr>
            <th className="py-1 pr-3 font-medium">Período</th>
            <th className="py-1 pr-3 font-medium">Diárias</th>
            <th className="py-1 pr-3 font-medium">Valor</th>
            <th className="py-1 pr-3 font-medium">Nota fiscal</th>
            <th className="py-1 pr-3 font-medium">Pagamento</th>
            <th className="py-1 font-medium">Taxa Faísca</th>
          </tr>
        </thead>
        <tbody>
          {parcelas.map((parcela) => (
            <tr key={`${parcela.inicio}-${parcela.fim}`} className="border-t">
              <td className="py-1 pr-3">
                {dataCurta(parcela.inicio)} a {dataCurta(parcela.fim)}
              </td>
              <td className="py-1 pr-3">{parcela.diarias}</td>
              <td className="py-1 pr-3">{formatCentsBRL(parcela.valor_cents)}</td>
              <td className="py-1 pr-3">{dataCurta(parcela.emissao)}</td>
              <td className="py-1 pr-3">{dataCurta(parcela.pagamento)}</td>
              <td className="py-1">
                {formatCentsBRL(parcela.repasse_cents)} em {dataCurta(parcela.repasse_previsto)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** A plataforma recebe o total da agência e paga o profissional, já sem a comissão. */
function retemComissao(item: PropostaItem): boolean {
  return item.modo_agencia === "total" && item.modo_freelancer === "reter";
}

function AjudaDaDataCombinada({
  combinada,
  regra,
  disabled,
  onVoltar,
}: {
  combinada: boolean;
  regra: string;
  disabled: boolean;
  onVoltar: () => void;
}) {
  if (!combinada) return <>{regra}</>;
  return (
    <button type="button" className="underline underline-offset-2" disabled={disabled} onClick={onVoltar}>
      Voltar para {regra}
    </button>
  );
}

function PagamentosDoProfissional({
  pagamentos,
  empresa,
}: {
  pagamentos: NonNullable<PropostaItem["previa_invite"]>["pagamentos_freelancer"];
  empresa: string;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left tabular-nums">
        <thead className="text-muted-foreground">
          <tr>
            <th className="py-1 pr-3 font-medium">Período</th>
            <th className="py-1 pr-3 font-medium">Diárias</th>
            <th className="py-1 pr-3 font-medium">NF contra a {empresa}</th>
            <th className="py-1 pr-3 font-medium">Envio da NF até</th>
            <th className="py-1 font-medium">Pagamento</th>
          </tr>
        </thead>
        <tbody>
          {pagamentos.map((pagamento) => (
            <tr key={`${pagamento.inicio}-${pagamento.fim}`} className="border-t">
              <td className="py-1 pr-3">
                {dataCurta(pagamento.inicio)} a {dataCurta(pagamento.fim)}
              </td>
              <td className="py-1 pr-3">{pagamento.diarias}</td>
              <td className="py-1 pr-3">{formatCentsBRL(pagamento.valor_cents)}</td>
              <td className="py-1 pr-3">{dataCurta(pagamento.envio_nf)}</td>
              <td className="py-1">{dataCurta(pagamento.pagamento)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function apenasDigitos(valor: string): string {
  return valor.replace(/\D/g, "");
}

interface DocumentoDivergenteProps {
  clienteId: string;
  documentoNosso: string;
  documentoOmie: string;
}

/** O CNPJ do cadastro e o do cliente no Omie não batem: escolhe qual dos dois vale nos dois lados. */
function DocumentoDivergente({ clienteId, documentoNosso, documentoOmie }: DocumentoDivergenteProps) {
  const { toast } = useToast();
  const enviar = useEnviarDocumentoAoOmie();
  const trazer = useTrazerDocumentoDoOmie();
  const ocupado = enviar.isPending || trazer.isPending;

  const executar = (acao: typeof enviar, sucesso: string) =>
    acao.mutate(clienteId, {
      onSuccess: () => toast({ title: sucesso }),
      onError: (erro) => {
        const formatado = formatApiError(erro);
        toast({ variant: "destructive", title: formatado.title, description: formatado.detail });
      },
    });

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
      <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
      <span className="min-w-0 flex-1">
        O CNPJ do cadastro ({documentoNosso}) é diferente do que está no Omie ({documentoOmie}).
      </span>
      {ocupado && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
      <Button
        variant="outline"
        size="sm"
        className="h-7 gap-1 bg-background px-2 text-xs"
        disabled={ocupado}
        onClick={() => executar(enviar, "CNPJ atualizado no Omie")}
      >
        <Upload className="h-3 w-3" />
        Atualizar no Omie
      </Button>
      <Button
        variant="outline"
        size="sm"
        className="h-7 gap-1 bg-background px-2 text-xs"
        disabled={ocupado}
        onClick={() => executar(trazer, "CNPJ do Omie trazido para o cadastro")}
      >
        <Download className="h-3 w-3" />
        Trazer do Omie
      </Button>
    </div>
  );
}

function LinkDoCadastro({
  campo,
  onEditar,
}: {
  campo: CampoDoCadastro;
  onEditar?: (campo: CampoDoCadastro) => void;
}) {
  if (!onEditar) return null;
  return (
    <button
      type="button"
      className="font-medium text-primary hover:underline"
      onClick={() => onEditar(campo)}
    >
      Editar no cadastro
    </button>
  );
}

/** Dado que só existe no cadastro da agência: aparece aqui travado, e o link abre o cadastro no campo certo. */
function CampoDoCadastroDaAgencia({
  label,
  campo,
  valor,
  vazio = "Não preenchido",
  className,
  onEditar,
}: {
  label: string;
  campo: CampoDoCadastro;
  valor: string | null;
  vazio?: string;
  className?: string;
  onEditar?: (campo: CampoDoCadastro) => void;
}) {
  return (
    <CampoEditavel
      label={label}
      className={className}
      valor={valor ?? ""}
      placeholder={vazio}
      disabled
      ajuda={<LinkDoCadastro campo={campo} onEditar={onEditar} />}
      onSalvar={() => undefined}
    />
  );
}

/** Mesmas frases dos modelos de proposta e invite na API (template_corisco e context). */
function ComoSaiNaProposta({ proposta }: { proposta: PropostaDetail }) {
  const { agencia, fatura_por_competencia_mensal: fatura, impostos_percentual: impostos } = proposta;
  const falta = (texto: string) => <span className={toneTextClasses.warning}>[{texto}]</span>;
  const dia = fatura.dia_emissao.em_uso;
  const prazo = fatura.prazo_pagamento_dias.em_uso;
  return (
    <ComoSaiNoInvite titulo="Como os dados da agência saem na proposta">
      <LinhaDoInvite rotulo="Código">{proposta.codigo ?? proposta.codigo_previsto ?? falta("código")}</LinhaDoInvite>
      <LinhaDoInvite rotulo="Identificação das partes">
        De outro lado, <strong>{identificacaoDaAgencia(agencia.razao_social, agencia.nome)}</strong>, pessoa jurídica de
        direito privado, inscrita no CNPJ sob nº <strong>{agencia.cnpj ?? falta("CNPJ da agência")}</strong>, com sede em{" "}
        <strong>{agencia.endereco ?? falta("endereço da agência")}</strong>, doravante CONTRATANTE.
      </LinhaDoInvite>
      <LinhaDoInvite rotulo="A/C (cabeçalho e assinatura)">{proposta.ac_contato ?? "Não aparece"}</LinhaDoInvite>
      {proposta.modalidade !== "fixa" && (
        <LinhaDoInvite rotulo="Impostos em cada período">
          {impostos.em_uso > 0
            ? `Valor impostos (${formatPercent(impostos.em_uso)}%), somado ao valor total`
            : "Não aparece: sem impostos"}
        </LinhaDoInvite>
      )}
      <LinhaDoInvite rotulo="Notas (no invite)">
        {fatura.em_uso
          ? `Uma por mês do período, emitida no dia ${dia ?? "[dia de emissão]"} do mês seguinte e paga ${prazo ?? "[prazo]"} dias depois`
          : "Uma por período"}
      </LinhaDoInvite>
      <p className="text-muted-foreground">E-mail e cargo do contato ficam só no cadastro: não saem no documento.</p>
    </ComoSaiNoInvite>
  );
}

const rotuloDasNotas = (porMes: boolean) => (porMes ? "Uma por mês do período" : "Uma por período");

/** Divisão das notas no cronograma do invite: segue a agência ou é escolhida na proposta. */
function CampoNotasDoInvite({
  fatura,
  disabled,
  onEscolher,
  onTornarPadraoDaAgencia,
  onEditarNoCadastro,
}: {
  fatura: FaturaPorCompetenciaMensal;
  disabled: boolean;
  onEscolher: (valor: boolean | null) => void;
  onTornarPadraoDaAgencia: (valor: boolean) => void;
  onEditarNoCadastro?: (campo: CampoDoCadastro) => void;
}) {
  const valor =
    fatura.escolhido === null ? NOTAS_DA_AGENCIA : fatura.escolhido ? NOTAS_POR_MES : NOTAS_POR_PERIODO;
  const diferenteDaAgencia = fatura.escolhido !== null && fatura.escolhido !== fatura.padrao_agencia;
  return (
    <div className="space-y-1.5">
      <Label htmlFor="campo-notas-do-invite" className="text-xs font-medium text-muted-foreground">
        Notas no invite
      </Label>
      <Select
        value={valor}
        disabled={disabled}
        onValueChange={(escolha) =>
          onEscolher(escolha === NOTAS_DA_AGENCIA ? null : escolha === NOTAS_POR_MES)
        }
      >
        <SelectTrigger id="campo-notas-do-invite">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NOTAS_DA_AGENCIA}>
            {rotuloDasNotas(fatura.padrao_agencia)} <span className="text-muted-foreground">(padrão da agência)</span>
          </SelectItem>
          <SelectItem value={NOTAS_POR_MES}>{rotuloDasNotas(true)}</SelectItem>
          <SelectItem value={NOTAS_POR_PERIODO}>{rotuloDasNotas(false)}</SelectItem>
        </SelectContent>
      </Select>
      {diferenteDaAgencia ? (
        <p className="text-[11px] leading-snug text-muted-foreground">
          Diferente do padrão da agência.{" "}
          <button
            type="button"
            disabled={disabled}
            className="font-medium underline underline-offset-2"
            onClick={() => onTornarPadraoDaAgencia(fatura.em_uso)}
          >
            Usar como padrão da agência
          </button>
        </p>
      ) : (
        <p className="text-[11px] leading-snug text-muted-foreground">
          Por mês quando a agência fatura por competência mensal.{" "}
          <LinkDoCadastro campo="fatura_por_competencia_mensal" onEditar={onEditarNoCadastro} />
        </p>
      )}
    </div>
  );
}

type CampoDaCompetenciaMensal = "dia_emissao_competencia" | "prazo_pagamento_competencia_dias";

/** Dia de emissão e prazo de pagamento da competência mensal: a agência sugere, a proposta pode mudar. */
function CamposDaCompetenciaMensal({
  fatura,
  disabled,
  destaque,
  onEscolher,
  onTornarPadraoDaAgencia,
  onEditarNoCadastro,
}: {
  fatura: FaturaPorCompetenciaMensal;
  disabled: boolean;
  destaque: boolean;
  onEscolher: (mudancas: Partial<Record<CampoDaCompetenciaMensal, number | null>>) => void;
  onTornarPadraoDaAgencia: (campo: CampoDaCompetenciaMensal, valor: number) => void;
  onEditarNoCadastro?: (campo: CampoDoCadastro) => void;
}) {
  const campos = [
    {
      campo: "dia_emissao_competencia" as const,
      label: "Dia de emissão da nota",
      prazo: fatura.dia_emissao,
      sufixo: undefined,
      explicacao: "Dia do mês seguinte ao trabalhado",
      valido: (numero: number) => numero >= 1 && numero <= 31,
    },
    {
      campo: "prazo_pagamento_competencia_dias" as const,
      label: "Prazo de pagamento da agência",
      prazo: fatura.prazo_pagamento_dias,
      sufixo: "dias",
      explicacao: "Dias entre a nota e o pagamento",
      valido: (numero: number) => numero >= 0 && numero <= 365,
    },
  ];
  return (
    <>
      {campos.map(({ campo, label, prazo, sufixo, explicacao, valido }) => {
        const diferenteDaAgencia = prazo.escolhido !== null && prazo.escolhido !== prazo.padrao_agencia;
        return (
          <CampoEditavel
            key={campo}
            id={`campo-${campo}`}
            label={label}
            valor={prazo.em_uso == null ? "" : String(prazo.em_uso)}
            sufixo={sufixo}
            inputMode="numeric"
            placeholder={fatura.em_uso ? "Informe" : "Sem padrão"}
            disabled={disabled || !fatura.em_uso}
            destaque={fatura.em_uso && destaque && prazo.em_uso == null}
            ajuda={
              !fatura.em_uso ? (
                <>
                  Só vale com uma nota por mês.{" "}
                  <LinkDoCadastro campo={campo} onEditar={onEditarNoCadastro} />
                </>
              ) : diferenteDaAgencia && prazo.em_uso != null ? (
                <>
                  Diferente do padrão da agência.{" "}
                  <button
                    type="button"
                    disabled={disabled}
                    className="font-medium underline underline-offset-2"
                    onClick={() => onTornarPadraoDaAgencia(campo, prazo.em_uso as number)}
                  >
                    Usar como padrão da agência
                  </button>
                </>
              ) : (
                <>
                  {prazo.padrao_agencia != null ? `${explicacao}, sugestão da agência. ` : `${explicacao}; a agência não tem sugestão. `}
                  <LinkDoCadastro campo={campo} onEditar={onEditarNoCadastro} />
                </>
              )
            }
            onSalvar={(valor) => {
              if (valor.trim() === "") {
                onEscolher({ [campo]: null });
                return;
              }
              const numero = numeroOuNulo(valor);
              if (numero == null || !Number.isInteger(numero) || !valido(numero)) return;
              onEscolher({ [campo]: numero === prazo.padrao_agencia ? null : numero });
            }}
          />
        );
      })}
    </>
  );
}

/** Impostos sobre profissional + plataforma: seguem a agência ou são escolhidos na proposta (0 tira). */
function CampoImpostos({
  impostos,
  disabled,
  onEscolher,
  onTornarPadraoDaAgencia,
  onEditarNoCadastro,
}: {
  impostos: ImpostosDaProposta;
  disabled: boolean;
  onEscolher: (valor: number | null) => void;
  onTornarPadraoDaAgencia: (valor: number) => void;
  onEditarNoCadastro?: (campo: CampoDoCadastro) => void;
}) {
  const padraoDaAgencia = impostos.padrao_agencia ?? 0;
  const diferenteDaAgencia = impostos.escolhido !== null && impostos.escolhido !== padraoDaAgencia;
  return (
    <CampoEditavel
      label="Impostos"
      valor={impostos.em_uso ? formatPercent(impostos.em_uso) : ""}
      sufixo="%"
      inputMode="decimal"
      placeholder="Sem impostos"
      disabled={disabled}
      ajuda={
        diferenteDaAgencia ? (
          <>
            Diferente do padrão da agência.{" "}
            <button
              type="button"
              disabled={disabled}
              className="font-medium underline underline-offset-2"
              onClick={() => onTornarPadraoDaAgencia(impostos.em_uso)}
            >
              Usar como padrão da agência
            </button>
          </>
        ) : (
          <>
            Sobre profissional + plataforma, à parte no total.{" "}
            <LinkDoCadastro campo="impostos_percentual" onEditar={onEditarNoCadastro} />
          </>
        )
      }
      onSalvar={(valor) => {
        const numero = valor.trim() === "" ? 0 : numeroOuNulo(valor);
        if (numero == null || numero < 0 || numero > 100) return;
        onEscolher(numero === padraoDaAgencia ? null : numero);
      }}
    />
  );
}
